/**
 * AUT: Recuperar Contraseña
 * Solicita email y envía link de recuperación.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Mail, CheckCircle2 } from 'lucide-react'
import { usePasswordResetRequest } from '@/modules/auth/hooks/useAuth'
import { Button } from '@/components/Button'

const schema = z.object({
  email: z.string().min(1, 'El email es requerido').email('Falta un email válido'),
})
type FormData = z.infer<typeof schema>

export function ForgotPasswordPage() {
  const { mutate: requestReset, isPending } = usePasswordResetRequest()
  const [submitted, setSubmitted] = useState(false)

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const onSubmit = (data: FormData) => {
    requestReset(data.email, {
      onSuccess: () => setSubmitted(true),
      onError: () => setSubmitted(true), // Por seguridad, siempre mostramos éxito
    })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-auth px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/clobi-logo-white.svg" alt="Clobi" className="h-32 mx-auto mb-4" />
          <p className="text-content-subtle mt-1 text-sm">Renace tu empresa. Opera en la nube.</p>
        </div>

        <div className="bg-surface rounded-2xl shadow-2xl p-8">
          {submitted ? (
            <div className="text-center py-4">
              <CheckCircle2 size={48} className="mx-auto text-success-solid mb-4" />
              <h2 className="text-xl font-semibold text-content mb-2">Revisa tu correo</h2>
              <p className="text-sm text-content-muted mb-6">
                Si existe una cuenta con ese email, recibirás un enlace para restablecer tu contraseña.
              </p>
              <Link
                to="/auth/login"
                className="text-sm text-primary hover:text-primary-hover hover:underline"
              >
                ← Volver al inicio de sesión
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-content mb-2">Recuperar contraseña</h2>
              <p className="text-sm text-content-muted mb-6">
                Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña.
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-content mb-1.5">
                    Email
                  </label>
                  <div className="relative">
                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
                    <input
                      {...register('email')}
                      id="email"
                      type="email"
                      autoComplete="email"
                      autoFocus
                      placeholder="tu@empresa.com"
                      className={`w-full pl-9 pr-4 py-2.5 rounded-lg border text-content placeholder:text-content-subtle
                        text-sm transition-colors outline-none
                        focus:ring-2 focus:ring-primary focus:border-primary
                        ${errors.email
                          ? 'border-danger-border-strong bg-danger-surface focus:ring-danger-solid focus:border-danger-solid'
                          : 'border-divider-strong bg-surface hover:border-divider-strong'
                        }`}
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-xs text-danger-content-soft">{errors.email.message}</p>
                  )}
                </div>

                <Button type="submit" size="lg" fullWidth isPending={isPending} pendingLabel="Enviando...">
                  Enviar enlace de recuperación
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link to="/auth/login" className="text-sm text-primary hover:text-primary-hover hover:underline">
                  ← Volver al inicio de sesión
                </Link>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-content-muted text-xs mt-6">
          © 2026 Clobi by zen · Todos los derechos reservados
        </p>
      </div>
    </div>
  )
}
